/**
 * Boot-time cache-buster.
 *
 * The Capacitor Android WebView caches index.html aggressively (its own
 * HTTP cache lives outside our Service Worker). This module polls
 * `/version.json` and force-reloads the WebView when the build id
 * changes — so a single Emergent deploy is the only step needed to
 * push fresh code to every installed phone.
 *
 * Strategy:
 *   1. On boot, persist the latest-fetched build id to localStorage.
 *      If a stored build id from the previous run differs from the
 *      one just fetched → we know the user is sitting on a stale
 *      WebView-cached HTML/JS bundle and we hard-reload immediately
 *      with a cache-busting query string so the WebView re-fetches
 *      index.html from the network instead of its HTTP cache.
 *   2. On every visibility-change → "visible" we re-fetch.
 *      If the build id differs → cache-bust reload.
 *   3. Also re-checks every 5 minutes for long-running sessions
 *      (a seller running a 4-hour live show without backgrounding).
 *
 * The cache-bust is a query string `?cb=<buildId>` appended to the
 * current URL. Most Android WebViews treat a changed query string as
 * a different resource and bypass their HTTP cache on the navigation.
 */
const STORAGE_KEY = "bidorah.build";
let knownBuild = null;
let reloading = false;

async function fetchBuildId() {
  try {
    const res = await fetch(
      `/version.json?_=${Date.now()}`,
      { cache: "no-store", credentials: "omit" },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data?.build || null;
  } catch {
    return null;
  }
}

function hardReload(latest) {
  if (reloading) return;
  reloading = true;
  try { localStorage.setItem(STORAGE_KEY, latest); } catch { /* ignore */ }
  try {
    const u = new URL(window.location.href);
    u.searchParams.set("cb", latest);
    // Use location.replace so the cache-busted URL doesn't pollute
    // browser/back-button history. WebView treats the changed query
    // string as a cache miss and re-fetches index.html from network.
    window.location.replace(u.toString());
  } catch {
    try { window.location.reload(true); } catch { window.location.reload(); }
  }
}

async function check({ allowImmediate = false } = {}) {
  const latest = await fetchBuildId();
  if (!latest) return;

  // Bootstrap path — first time this function runs in the lifetime of
  // the page. We compare the freshly fetched build against any value
  // persisted from the *previous* WebView session (which may have
  // happened before a deploy went out). If they differ, the user is
  // running stale cached HTML/JS and we cache-bust reload right now.
  if (!knownBuild) {
    let stored = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
    knownBuild = latest;
    try { localStorage.setItem(STORAGE_KEY, latest); } catch { /* ignore */ }
    if (allowImmediate && stored && stored !== latest) {
      hardReload(latest);
    }
    return;
  }

  if (knownBuild !== latest) {
    hardReload(latest);
  }
}

export function installVersionCheck() {
  if (typeof window === "undefined") return;
  if (window.__bidoraVersionCheckInstalled) return;
  window.__bidoraVersionCheckInstalled = true;

  // First read — cross-checks the persisted "last build I saw" against
  // the freshly fetched build id. Triggers a hard reload on cold start
  // if a new deploy happened while the app was closed.
  check({ allowImmediate: true });

  // Re-check whenever the app comes back to the foreground.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });

  // Also poll every 5 minutes for very long-running sessions.
  setInterval(check, 5 * 60 * 1000);
}
